import { Component, ViewChild, computed, effect, inject, input } from '@angular/core';
import { faCalendar, faClock, faHome } from '@fortawesome/pro-regular-svg-icons';
import { CanLeaveGuardComponent } from '@pflegix/guard';
import { BusinessPartnerResponseDto } from '@pflegix/types';
import {
  AppIcon,
  AppIconEntity,
  AppIconEntityIconName,
  AppIconService,
  DashboardLayoutComponent,
  DialogComponent,
  PanelComponent,
  PanelTemplateType,
  PfxTemplateDirective,
} from '@pflegix/ui';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService } from 'primeng/api';
import { isDependent, isIndividual, isInternalCaregiver } from '../../../../../../feature/business-partner-management';
import { NotesFormComponent } from '../../../../../../feature/business-partner-management/notes-form/notes-form.component';
import { BusinessPartnerStore } from '../../../../../../feature/business-partner-management/store';
import { DocumentationActiveCaregiverStatsComponent } from '../../../../../../feature/documentation-management/documentation-stats/documentation-active-caregiver-stats.component';
import { DocumentationCareConsultationStatsComponent } from '../../../../../../feature/documentation-management/documentation-stats/documentation-care-consultation-stats.component';
import { TaskListWidgetComponent } from '../../../../../../feature/task-management/task-list-widget';

@Component({
  selector: 'pfx-overview-view',
  imports: [
    DashboardLayoutComponent,
    PanelComponent,
    TaskListWidgetComponent,
    PfxTemplateDirective,
    AccordionModule,
    DocumentationActiveCaregiverStatsComponent,
    DocumentationCareConsultationStatsComponent,
    DialogComponent,
    NotesFormComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './overview-view.component.html',
})
export class OverviewViewComponent extends CanLeaveGuardComponent {
  private readonly appIconService = inject(AppIconService);

  public businessPartnerId = input.required<string>();

  @ViewChild(NotesFormComponent)
  public form!: NotesFormComponent;

  protected isIndividual = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner ? isIndividual(businessPartner) : false;
  });

  protected isDependent = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner ? isDependent(businessPartner) : false;
  });

  protected isInternalCaregiver = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner ? isInternalCaregiver(businessPartner) : false;
  });

  private readonly store = inject(BusinessPartnerStore);

  protected businessPartner = computed<BusinessPartnerResponseDto | undefined>(
    () => this.store.entityMap()[this.businessPartnerId() ?? '']
  );

  protected taskListWidgetFilter = computed(
    // TODO get userId of businessPartner () => `or$references.businessPartner;eq;${this.businessPartnerId()};;editor;eq;63dcbf96e2a5b8fbd087fc4d`
    () => `references.businessPartner$eq$${this.businessPartnerId()}`
  );

  protected urlToTicketTab = computed(() => `/business-partner-center/${this.businessPartnerId()}/tickets`);

  protected PanelTemplateType = PanelTemplateType;

  protected icons = {
    futureAppointment: this.getAppointmentIcon('futureAppointment'),
    pastAppointment: this.getAppointmentIcon('pastAppointment'),
    conflictingAppointment: this.getAppointmentIcon('conflictingAppointment'),
    deletedAppointment: this.getAppointmentIcon('deletedAppointment'),
    orderWithFutureAppointment: this.getOrderIcon('orderWithFutureAppointment'),
    orderWithPastAppointment: this.getOrderIcon('orderWithPastAppointment'),
    orderWithoutAppointment: this.getOrderIcon('orderWithoutAppointment'),
    closedOrder: this.getOrderIcon('closedOrder'),
  };

  protected faHome = faHome;

  protected faClock = faClock;

  protected faCalendar = faCalendar;

  constructor() {
    super();

    // Use effect to automatically load business partner when businessPartnerId changes
    effect(() => {
      const businessPartnerId = this.businessPartnerId();

      this.store.loadById(businessPartnerId);
    });
  }

  protected getAppointmentIcon(iconName?: AppIconEntityIconName<AppIconEntity.APPOINTMENT>): AppIcon | undefined {
    return this.appIconService.getAppointmentIcon(iconName);
  }

  public getOrderIcon(iconName?: AppIconEntityIconName<AppIconEntity.ORDER>): AppIcon | undefined {
    return this.appIconService.getOrderIcon(iconName);
  }

  public canDeactivate(): boolean {
    return this.form.isPristine;
  }
}
